import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  CalendarClock,
  BellRing,
  Bookmark,
  Check,
  CheckCircle2,
  FileText,
  Forward,
  Headphones,
  Mic,
  MicOff,
  MessageCircleMore,
  MessagesSquare,
  Paperclip,
  PhoneOff,
  PhoneCall,
  Search,
  Sparkles,
  UsersRound,
  X,
} from "lucide-react";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";
import Reveal from "./Reveal";
import Breadcrumbs from "./Breadcrumbs";



const features = [
  {
    icon: MessageCircleMore,
    title: "Team Messaging",
    copy: "Keep day-to-day conversations moving in one fast, focused workspace.",
    visual: "messages",
  },
  {
    icon: MessagesSquare,
    title: "Group Conversations",
    copy: "Bring teams and shared discussions together without losing the thread.",
    visual: "groups",
  },
  {
    icon: PhoneCall,
    title: "Audio Calls",
    copy: "Move from typing to a live audio conversation whenever talking is faster.",
    visual: "calls",
  },
  {
    icon: Paperclip,
    title: "File Sharing",
    copy: "Share files beside the conversation and people that give them context.",
    visual: "files",
  },
  {
    icon: Search,
    title: "Smart Search",
    copy: "Find messages, people, and shared details without scrolling through long threads.",
    visual: "search",
  },
  {
    icon: BellRing,
    title: "Live Presence",
    copy: "See availability and active conversations so communication feels immediate.",
    visual: "presence",
  },
  {
    icon: Forward,
    title: "Message Forwarding",
    copy: "Move important context into the next conversation without starting from zero.",
    visual: "forward",
  },
  {
    icon: Bookmark,
    title: "Connected My Space",
    copy: "Keep reminders, notes, bookmarks, and priorities close to your conversations.",
    visual: "space",
  },
];

function FeatureVisual({ type }) {
  if (type === "messages") {
    return (
      <div className="msg-feature-mock msg-feature-mock--messages" aria-hidden="true">
        <span className="msg-mini-label">Software Team</span>
        <div className="msg-bubble msg-bubble--left">Can we review the latest update?</div>
        <div className="msg-bubble msg-bubble--right">Yes, sharing it now.</div>
      </div>
    );
  }

  if (type === "groups") {
    return (
      <div className="msg-feature-mock msg-feature-mock--groups" aria-hidden="true">
        {["IT Team", "Product", "Support"].map((name, index) => (
          <div className="msg-group-row" key={name}>
            <span className="msg-group-avatar"><UsersRound size={13} /></span>
            <span>{name}</span>
            <i>{index + 2}</i>
          </div>
        ))}
      </div>
    );
  }

  if (type === "calls") {
    return (
      <div className="msg-feature-mock msg-feature-mock--calls" aria-hidden="true">
        <div className="msg-call-ring"><Headphones size={28} /></div>
        <strong>Audio call</strong>
        <span>Connected · 04:28</span>
        <div className="msg-call-bars"><i /><i /><i /><i /><i /></div>
      </div>
    );
  }

  if (type === "files") {
    return (
      <div className="msg-feature-mock msg-feature-mock--files" aria-hidden="true">
        {["MessageInput.css", "MySpacePanel.jsx", "image.png"].map((name, index) => (
          <div className="msg-file-row" key={name}>
            <FileText size={15} />
            <span>{name}</span>
            <small>{["20.9 KB", "88.9 KB", "1.4 MB"][index]}</small>
          </div>
        ))}
      </div>
    );
  }

  if (type === "search") {
    return (
      <div className="msg-feature-mock msg-feature-mock--search" aria-hidden="true">
        <div className="msg-search-box"><Search size={14} /> <span>remote access</span></div>
        <div className="msg-search-result"><b>Software Team</b><span>...remote access is connected...</span></div>
        <div className="msg-search-result"><b>Priyanka</b><span>Remote session notes</span></div>
      </div>
    );
  }

  if (type === "presence") {
    return (
      <div className="msg-feature-mock msg-feature-mock--presence" aria-hidden="true">
        {["Harshit", "Priyanka", "Vidyashree"].map((name, index) => (
          <div className="msg-presence-row" key={name}>
            <span className={`msg-presence-avatar msg-presence-avatar--${index + 1}`}>{name[0]}</span>
            <span><b>{name}</b><small>{index === 2 ? "Away" : "Available"}</small></span>
            <i className={index === 2 ? "away" : ""} />
          </div>
        ))}
      </div>
    );
  }

  if (type === "forward") {
    return (
      <div className="msg-feature-mock msg-feature-mock--forward" aria-hidden="true">
        <div className="msg-forward-source">Please review the new support workflow.</div>
        <div className="msg-forward-arrow"><Forward size={18} /></div>
        <div className="msg-forward-target"><Check size={14} /> Forwarded to IT Team</div>
      </div>
    );
  }

  return (
    <div className="msg-feature-mock msg-feature-mock--space" aria-hidden="true">
      <div><BellRing size={15} /><span>Reminders</span><b>3</b></div>
      <div><FileText size={15} /><span>Sticky Notes</span><b>2</b></div>
      <div><Bookmark size={15} /><span>Bookmarks</span><b>3</b></div>
    </div>
  );
}

function ProductCallout({ icon: Icon, title, copy, side, className = "", active = false, onClick }) {
  return (
    <button
      type="button"
      className={`msg-workspace-callout msg-workspace-callout--${side} ${active ? "is-active" : ""} ${className}`}
      onClick={onClick}
      aria-pressed={active}
    >
      <span><Icon size={18} /></span>
      <div>
        <strong>{title}</strong>
        <p>{copy}</p>
        <small>{active ? "Playing now" : "Auto preview"}</small>
      </div>
    </button>
  );
}

const spaceFeed = [
  { type: "reminder", title: "Follow up with Support", meta: "Today · 4:30 PM" },
  { type: "task", title: "Review messaging release", meta: "High priority" },
  { type: "task", title: "Send workspace update", meta: "Today" },
  { type: "reminder", title: "Team check-in", meta: "Tomorrow · 10:00 AM" },
];

function MySpaceDemo({ onClose }) {
  const [spaceItemCount, setSpaceItemCount] = useState(1);
useEffect(() => {
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });

  document.title =
    "Team Collaboration Software for Modern Teams | Cullath Connect";

  const description =
    "Cullath Connect is team collaboration software with team messaging, group conversations, audio calls, file sharing, and smart search for modern teams.";

  // Meta description
  let metaDescription = document.querySelector(
    'meta[name="description"]'
  );

  if (!metaDescription) {
    metaDescription = document.createElement("meta");
    metaDescription.setAttribute("name", "description");
    document.head.appendChild(metaDescription);
  }

  metaDescription.setAttribute("content", description);

  // Canonical URL
  const canonicalUrl = "https://cullath.com/connect";

  let canonical = document.querySelector(
    'link[rel="canonical"]'
  );

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute("href", canonicalUrl);

  // Schema
  const schemas = [
    // ... your existing schemas
  ];

  const schemaElements = schemas.map((schema, index) => {
    const script = document.createElement("script");

    script.type = "application/ld+json";
    script.setAttribute(
      "data-cullath-connect-schema",
      String(index)
    );
    script.textContent = JSON.stringify(schema);

    document.head.appendChild(script);

    return script;
  });

  return () => {
    schemaElements.forEach((script) => script.remove());

    // Optional: remove canonical when leaving the page
    if (canonical) {
      canonical.remove();
    }
  };
}, []);


  return (
    <div className="msg-workspace-demo msg-workspace-demo--space" aria-live="polite">
      <div className="msg-demo-space__head">
        <div>
          <span className="msg-demo-eyebrow">MY SPACE</span>
          <h4>Personal follow-up</h4>
        </div>
        <button type="button" className="msg-demo-close msg-demo-close--inline" onClick={onClose} aria-label="Close My Space preview"><X size={16} /></button>
      </div>
      <div className="msg-demo-space__summary">
        <span><CalendarClock size={15} /> Reminders</span>
        <b>{Math.min(spaceItemCount, 2)}</b>
        <span><CheckCircle2 size={15} /> To-do</span>
        <b>{Math.max(0, spaceItemCount - 2)}</b>
      </div>
      <div className="msg-demo-space__feed">
        {spaceFeed.slice(0, spaceItemCount).map((item, index) => (
          <article className={`msg-demo-space__item msg-demo-space__item--${item.type}`} key={item.title}>
            <span>{item.type === "reminder" ? <BellRing size={16} /> : <CheckCircle2 size={16} />}</span>
            <div><strong>{item.title}</strong><small>{item.meta}</small></div>
            <i>{index + 1}</i>
          </article>
        ))}
        {spaceItemCount < spaceFeed.length && <div className="msg-demo-space__adding"><span /> Adding next item...</div>}
      </div>
    </div>
  );
}

function WorkspaceDemo({ mode, onClose }) {
  if (!mode) return null;

  if (mode === "call") {
    return (
      <div className="msg-workspace-demo msg-workspace-demo--call" aria-live="polite">
        <button type="button" className="msg-demo-close" onClick={onClose} aria-label="Close audio call preview"><X size={16} /></button>
        <div className="msg-demo-call__avatar">P</div>
        <span className="msg-demo-eyebrow">AUDIO CALL</span>
        <h4>Priyanka Das</h4>
        <p>Connected · 00:18</p>
        <div className="msg-demo-wave" aria-hidden="true">
          {Array.from({ length: 14 }).map((_, index) => <i key={index} style={{ animationDelay: `${index * 70}ms` }} />)}
        </div>
        <div className="msg-demo-call__actions">
          <button type="button" aria-label="Mute preview"><Mic size={19} /></button>
          <button type="button" className="is-muted" aria-label="Muted microphone preview"><MicOff size={19} /></button>
          <button type="button" className="is-end" aria-label="End call preview" onClick={onClose}><PhoneOff size={19} /></button>
        </div>
      </div>
    );
  }

  if (mode === "space") {
    return <MySpaceDemo onClose={onClose} />;
  }

  const isGroup = mode === "groups";
  return (
    <div className="msg-workspace-demo msg-workspace-demo--chat" aria-live="polite">
      <div className="msg-demo-chat__head">
        <div className="msg-demo-chat__avatar">{isGroup ? <UsersRound size={19} /> : "H"}</div>
        <div><span className="msg-demo-eyebrow">{isGroup ? "TEAM GROUP" : "LIVE CHAT"}</span><h4>{isGroup ? "Software Team" : "Harshit Degul"}</h4></div>
        <span className="msg-demo-live"><i /> Live</span>
        <button type="button" className="msg-demo-close msg-demo-close--inline" onClick={onClose} aria-label="Close chat preview"><X size={16} /></button>
      </div>
      <div className="msg-demo-chat__messages">
        <div className="msg-demo-message msg-demo-message--in"><b>{isGroup ? "Priyanka" : "Harshit"}</b><span>Can we review the new messaging update?</span><small>10:30 AM</small></div>
        <div className="msg-demo-message msg-demo-message--out"><span>Yes. I am checking the workspace now.</span><small>10:31 AM ✓✓</small></div>
        <div className="msg-demo-message msg-demo-message--in msg-demo-message--last"><b>{isGroup ? "Vidyashree" : "Harshit"}</b><span>{isGroup ? "I shared the latest notes with the team." : "Perfect. The live chat looks good."}</span><small>10:32 AM</small></div>
        <div className="msg-demo-typing"><i /><i /><i /><span>{isGroup ? "Priyanka" : "Harshit"} is typing</span></div>
      </div>
      <div className="msg-demo-chat__composer"><span>Write a message...</span><button type="button"><ArrowRight size={16} /></button></div>
    </div>
  );
}

const modernCards = [
  {
    icon: MessageCircleMore,
    title: "Communicate Faster",
    copy: "Keep conversations moving with instant messaging, team groups, and audio calls.",
  },
  {
    icon: UsersRound,
    title: "Collaborate Naturally",
    copy: "Share files, discuss ideas, and keep the right people close to the work.",
  },
  {
    icon: Sparkles,
    title: "Stay Focused",
    copy: "Keep personal reminders, notes, bookmarks, and priorities alongside your conversations.",
  },
  {
    icon: Check,
    title: "Keep Context Connected",
    copy: "Move between chat, audio calls, shared files, and follow-up without rebuilding context.",
  },
];

const WORKSPACE_SEQUENCE = ["chat", "groups", "call", "space"];
const WORKSPACE_MODE_DURATIONS = {
  chat: 3600,
  groups: 3600,
  call: 3200,
  space: 4200,
};

export default function MessagingPage() {
  const workspaceSectionRef = useRef(null);
  const [workspaceMode, setWorkspaceMode] = useState(null);
  const [workspaceInView, setWorkspaceInView] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
     document.title = "Team Collaboration Software for Modern Teams | Cullath Connect";

  const description =
    "Cullath Connect is team collaboration software with team messaging, group conversations, audio calls, file sharing, and smart search for modern teams..";

    const metaDescription = document.querySelector(
    'meta[name="description"]'
  );

  if (!metaDescription) {
    metaDescription = document.createElement("meta");
    metaDescription.setAttribute("name", "description");
    document.head.appendChild(metaDescription);
  }

  metaDescription.setAttribute("content", description);

  // Canonical
  let canonical = document.querySelector(
    'link[rel="canonical"]'
  );

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute(
    "href",
    "https://cullath.com/connect"
  );

  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": "https://cullath.com/connect/#breadcrumb",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://cullath.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Connect",
          item: "https://cullath.com/connect",
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "@id": "https://cullath.com/connect/#software",
      name: "Cullath Connect",
      url: "https://cullath.com/connect",
      description,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      brand: {
        "@id": "https://cullath.com/#organization",
      },
      isPartOf: {
        "@id": "https://cullath.com/#website",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": "https://cullath.com/connect/#webpage",
      url: "https://cullath.com/connect",
      name: "Team Collaboration Software for Modern Teams | Cullath Connect",
      description,
      isPartOf: {
        "@id": "https://cullath.com/#website",
      },
      about: {
        "@id": "https://cullath.com/#organization",
      },
      inLanguage: "en-US",
    },
  ];

  const schemaElements = schemas.map((schema, index) => {
    const script = document.createElement("script");

    script.type = "application/ld+json";
    script.setAttribute(
      "data-cullath-connect-schema",
      String(index)
    );
    script.textContent = JSON.stringify(schema);

    document.head.appendChild(script);

    return script;
  });

  return () => {
    schemaElements.forEach((script) => script.remove());
  };
  }, []);

  useEffect(() => {
    const section = workspaceSectionRef.current;
    if (!section) return undefined;

    if (!("IntersectionObserver" in window)) {
      const fallbackTimer = window.setTimeout(() => {
        setWorkspaceInView(true);
        setWorkspaceMode("chat");
      }, 0);
      return () => window.clearTimeout(fallbackTimer);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setWorkspaceInView(entry.isIntersecting);
        if (entry.isIntersecting) {
          setWorkspaceMode((current) => current ?? "chat");
        }
      },
      { threshold: 0.35 },
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!workspaceInView || !workspaceMode) return undefined;

    const timer = window.setTimeout(() => {
      setWorkspaceMode((current) => {
        const currentIndex = WORKSPACE_SEQUENCE.indexOf(current);
        const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % WORKSPACE_SEQUENCE.length;
        return WORKSPACE_SEQUENCE[nextIndex];
      });
    }, WORKSPACE_MODE_DURATIONS[workspaceMode] ?? 3600);

    return () => window.clearTimeout(timer);
  }, [workspaceInView, workspaceMode]);

  const openWorkspaceDemo = (mode) => {
    setWorkspaceMode(mode);
  };

  return (
    <div className="messaging-page-shell">

      
      <SiteHeader isHeroPage />

      <main className="messaging-page">
        <section className="msg-hero" id="product">
          <div className="msg-hero-grid" aria-hidden="true" />
          <div className="msg-hero-glow msg-hero-glow--one" aria-hidden="true" />
          <div className="msg-hero-glow msg-hero-glow--two" aria-hidden="true" />

          <div className="msg-page-container msg-hero-layout">
            <Reveal className="msg-hero-copy">
              <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Products", href: "/#product" }, { label: "Connect" }]} />
              <div className="msg-product-pill"><MessageCircleMore size={17} /> Cullath Messaging</div>
              <h1 aria-label="Conversations that keep work moving.">
                <span className="msg-hero-title-line msg-hero-title-line--dark">
                  Team Collaboration Software
                </span>
                <span className="msg-hero-title-line msg-hero-title-line--green">
                  That Keeps Work Moving
                </span>
              </h1>
              <p>
                Cullath Connect is team collaboration software that brings team messaging, group conversations, audio calls, <a href="https://cullath.com/assist"
                  className="highlight-link">
                  file sharing
                </a>, search,
                and personal follow-up into one <a href="https://cullath.com/"
                  className="highlight-link">
                  connected workspace
                </a> for faster communication and collaboration.
              </p>
              <div className="msg-hero-actions">
                <a className="msg-primary-button" href="/contact-us">Request Demo<ArrowRight size={18} /></a>
                <a className="msg-secondary-button" href="https://connect.cullath.com/desktop/Cullath.exe">Download<ArrowRight size={17} /></a>
              </div>
              <div className="msg-hero-proof">
                <span><Check size={15} /> Direct and group chat</span>
                <span><Check size={15} /> Audio calls</span>
                <span><Check size={15} /> Files and My Space</span>
              </div>
            </Reveal>

            <Reveal className="msg-hero-stage" delay={120}>
              <div className="msg-stage-orbit msg-stage-orbit--one" aria-hidden="true" />
              <div className="msg-stage-orbit msg-stage-orbit--two" aria-hidden="true" />
              <div className="msg-stage-orbit msg-stage-orbit--three" aria-hidden="true" />

              <div className="msg-app-frame">
                <img
                  src="/cullath-connect-messaging-workspace.png"
                  alt="Cullath messaging workspace with chats, shared files, reminders, tasks, notes, and bookmarks"
                  width="1586"
                  height="992"
                  decoding="async"
                  fetchPriority="high"
                />
              </div>

              <div className="msg-float-chip msg-float-chip--chat"><MessageCircleMore size={17} /> Live conversations</div>
              <div className="msg-float-chip msg-float-chip--group"><UsersRound size={17} /> Team groups</div>
              <div className="msg-float-chip msg-float-chip--call"><PhoneCall size={17} /> Audio calls</div>
              <div className="msg-float-chip msg-float-chip--files"><Paperclip size={17} /> File sharing</div>
            </Reveal>
          </div>
        </section>

        <section className="msg-features" id="features">
          <div className="msg-page-container">
            <Reveal className="msg-section-heading msg-section-heading--left">
              <span className="msg-section-kicker">POWERFUL MESSAGING FEATURES</span>
              <h2>Everything You Need in Team <em>Collaboration Software.</em></h2>
              <p>
              From team messaging and group conversations to audio calls,  <a href="https://cullath.com/assist"
                  className="highlight-link">
                  file sharing
                </a> , and smart search, Cullath Connect brings every collaboration tool together in one  <a href="https://cullath.com/"
                  className="highlight-link">
                  connected workspace.
                </a> 
              </p>
            </Reveal>

            <div className="msg-feature-grid">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Reveal className="msg-feature-reveal" delay={(index % 4) * 70} key={feature.title}>
                    <article className={`msg-feature-card msg-feature-card--${feature.visual}`}>
                      <div className="msg-feature-card__content">
                        <span className="msg-feature-icon"><Icon size={23} /></span>
                        <h3>{feature.title}</h3>
                        <p>{feature.copy}</p>
                      </div>
                      <FeatureVisual type={feature.visual} />
                    </article>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        <section className="msg-workspace" id="workspace" ref={workspaceSectionRef}>
          <div className="msg-page-container">
            <Reveal className="msg-section-heading msg-section-heading--center">
              <span className="msg-section-kicker"><MessageCircleMore size={15} /> EXPLORE THE CULLATH WORKSPACE</span>
              <h2>One Team Collaboration Workspace. <em>All the Context Around It.</em></h2>
              <p>
                Bring conversations, teammates, shared files, and personal work together in one<a href="https://cullath.com/"
                  className="highlight-link">
                 team collaboration workspace 
                </a> instead of switching between disconnected apps and windows.
              </p>
            </Reveal>

            <Reveal className={`msg-workspace-stage ${workspaceMode ? "is-demo-active" : ""} ${workspaceMode ? `is-mode-${workspaceMode}` : ""}`} delay={100}>
              <div className="msg-workspace-frame">
                <div className="msg-workspace-frame__top">
                  <div><img src="/1.png" alt="" /> <strong>Cullath Messaging</strong></div>
                  <span><Search size={15} /> Search conversations</span>
                  <i>●</i>
                </div>
                <div className={`msg-workspace-app ${workspaceMode ? "is-demo-active" : ""}`}>
                  <img className="msg-workspace-image" src="/cullath-workspace-reference.png" alt="Cullath messaging workspace showing chats and My Space" />


                  <div className="msg-workspace-demo-hint"><span /> Auto demo: Chat → Groups → Calls → My Space</div>
                  <WorkspaceDemo mode={workspaceMode} onClose={() => setWorkspaceMode(null)} />
                </div>
              </div>

              <ProductCallout icon={MessageCircleMore} title="Direct conversations" copy="A live one-to-one conversation appears automatically inside the workspace." side="left" className="msg-workspace-callout--chat" active={workspaceMode === "chat"} onClick={() => openWorkspaceDemo("chat")} />
              <ProductCallout icon={UsersRound} title="Team groups" copy="The demo moves into a shared team conversation with live replies." side="left" className="msg-workspace-callout--groups" active={workspaceMode === "groups"} onClick={() => openWorkspaceDemo("groups")} />
              <ProductCallout icon={PhoneCall} title="Audio calls" copy="An animated audio-call window opens automatically after group chat." side="right" className="msg-workspace-callout--calls" active={workspaceMode === "call"} onClick={() => openWorkspaceDemo("call")} />
              <ProductCallout icon={Bookmark} title="My Space" copy="Reminders and to-do items are added one by one before the sequence repeats." side="right" className="msg-workspace-callout--space" active={workspaceMode === "space"} onClick={() => openWorkspaceDemo("space")} />
            </Reveal>
          </div>
        </section>

        <section className="msg-modern" id="why">
          <div className="msg-page-container">
            <Reveal className="msg-section-heading msg-section-heading--center">
              <span className="msg-section-kicker">BUILT FOR MODERN TEAMS</span>
              <h2>Designed for the Way <em>Modern Teams Communicate</em></h2>
              <p>
                Whether teams work in the office, remotely, or across locations, Cullath Connect keeps people, conversations, and follow-up together in one connected collaboration workspace.
              </p>
            </Reveal>

            <div className="msg-modern-grid">
              {modernCards.map((card, index) => {
                const Icon = card.icon;
                return (
                  <Reveal delay={(index % 2) * 90} key={card.title}>
                    <article className="msg-modern-card">
                      <div className="msg-modern-card__copy">
                        <span><Icon size={24} /></span>
                        <h3>{card.title}</h3>
                        <p>{card.copy}</p>
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>

            <Reveal className="msg-final-cta" delay={100}>
              <div className="msg-final-cta__copy">
                <span>WHERE WORK ORBITS.</span>
                <h2>Ready to Bring Every Conversation Closer to the Work?</h2>
                <p>See how Cullath Connect brings team messaging, collaboration, and conversations together in one connected workspace.</p>
              </div>
              <div className="msg-final-cta__actions">
                <a className="msg-cta-light" href="https://connect.cullath.com/desktop/Cullath.exe">Request Demo <ArrowRight size={18} /></a>
                <a className="msg-cta-ghost" href="./">Back to Cullath <ArrowRight size={17} /></a>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
